import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAuth } from "firebase/auth";
import {
  getFirestore, collection, addDoc, getDocs,
  deleteDoc, doc, updateDoc, query, orderBy, limit
} from "firebase/firestore";
import {
  AiFillStar, AiOutlineStar, AiOutlineComment,
  AiFillEdit, AiFillDelete
} from "react-icons/ai";
import { FaTimes, FaPaperPlane } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from "../context/AuthContext";

const Feedback = ({ isDashboard = false }) => {
  const auth = getAuth();
  const db = getFirestore();
  const { userProfile } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const colors = {
    bg: 'var(--bg-main)',
    accent: '#22c55e',
    card: 'var(--bg-card)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    glow: 'rgba(34, 197, 94, 0.15)'
  };

  const fetchFeedback = useCallback(async () => {
    try {
      const q = query(
        collection(db, "feedback"),
        orderBy("timestamp", "desc"),
        limit(24)
      );
      const querySnapshot = await getDocs(q);
      const feedbackData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      if (feedbackData.length === 0) {
        setFeedbackList(defaultTestimonials);
      } else {
        setFeedbackList(feedbackData);
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
      setFeedbackList(defaultTestimonials);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  useEffect(() => {
    const user = auth.currentUser;
    if (user && (user.email === "snaped4338@gmail.com" || user.email === process.env.REACT_APP_ADMIN_EMAIL)) {
      setIsAdmin(true);
    }
  }, [auth.currentUser, auth.currentUser?.email]);

  const handleSubmit = async () => {
    if (!rating || !message) {
      toast.error("Please provide rating and message");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      toast.error("Please sign in to leave feedback");
      return;
    }

    // Determine role dynamically
    const rawRole = userProfile?.role || "student";
    const displayRole = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);

    const feedbackData = {
      userId: user.uid,
      name: userProfile?.companyName || userProfile?.fullName || user.displayName || "Anonymous",
      photo: user.photoURL || null,
      rating,
      message,
      timestamp: new Date(),
      role: displayRole
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, "feedback", editingId), {
          rating,
          message,
          timestamp: new Date(),
        });
        toast.success("Feedback updated!");
      } else {
        await addDoc(collection(db, "feedback"), feedbackData);
        toast.success("Feedback submitted!");
      }
      setShowModal(false);
      setRating(0);
      setMessage("");
      setEditingId(null);
      fetchFeedback();
    } catch (error) {
      toast.error("Error submitting feedback");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this feedback?")) {
      try {
        await deleteDoc(doc(db, "feedback", id));
        setFeedbackList(prev => prev.filter(f => f.id !== id));
        toast.success("Feedback deleted");
      } catch (error) {
        toast.error("Error deleting feedback");
      }
    }
  };

  const handleEdit = (f) => {
    setEditingId(f.id);
    setRating(f.rating);
    setMessage(f.message);
    setShowModal(true);
  };

  const row1 = feedbackList.slice(0, Math.ceil(feedbackList.length / 2));
  const row2 = feedbackList.slice(Math.ceil(feedbackList.length / 2));

  const MarqueeRow = ({ items, reverse = false }) => (
    <div className="flex overflow-hidden py-4 select-none">
      <motion.div
        className="flex gap-6 px-4 min-w-max"
        animate={{ x: reverse ? [0, "-50%"] : ["-50%", 0] }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
      >
        {[...items, ...items, ...items].map((item, i) => (
          <div
            key={i}
            className="w-[300px] md:w-[350px] p-6 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl flex flex-col group hover:bg-white/10 transition-all duration-300"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <img src={item.photo || "https://img.icons8.com/bubbles/100/user.png"} alt="" className="w-12 h-12 rounded-2xl object-cover border border-green-500/20" />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-black flex items-center justify-center">
                  <div className="w-1 h-1 bg-white rounded-full animate-ping" />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-white leading-tight">{item.name}</h4>
                <p className="text-[11px] text-gray-500">{item.role || "IPU Student"}</p>
              </div>
              <div className="ml-auto flex gap-0.5">
                {[...Array(5)].map((_, starIdx) => (
                  <AiFillStar key={starIdx} className={`w-3.5 h-3.5 ${starIdx < (item.rating || 5) ? "text-green-500" : "text-gray-800"}`} />
                ))}
              </div>
            </div>
            <p className="text-gray-300 text-[13px] leading-relaxed line-clamp-4">"{item.message}"</p>
            {isAdmin && item.id && (
              <div className="flex gap-2 mt-4 self-end opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => { e.stopPropagation(); handleEdit(item); }} className="p-1.5 text-green-400 hover:bg-green-400/10 rounded-lg"><AiFillEdit size={16} /></button>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="p-1.5 text-red-500 hover:bg-red-500/10 rounded-lg"><AiFillDelete size={16} /></button>
              </div>
            )}
          </div>
        ))}
      </motion.div>
    </div>
  );

  return (
    <div style={{ background: colors.bg, minHeight: isDashboard ? 'auto' : '100vh', color: '#fff', position: 'relative' }}>
      <ToastContainer position="top-right" theme="dark" pauseOnFocusLoss={false} />

      <section className={`${isDashboard ? 'pt-10' : 'pt-32'} pb-20 px-4 overflow-hidden relative`}>
        {/* Background Decorative Elements */}
        {!isDashboard && (
          <>
            <div className="absolute top-1/4 -right-20 w-[600px] h-[600px] bg-green-500/10 blur-[150px] rounded-full pointer-events-none" />
            <div className="absolute bottom-1/4 -left-20 w-[600px] h-[600px] bg-indigo-500/5 blur-[150px] rounded-full pointer-events-none" />
          </>
        )}

        <div className="max-w-7xl mx-auto text-center mb-20 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold uppercase tracking-widest mb-8"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Community Feedback
            </motion.div>

            <h1 className={`${isDashboard ? 'text-4xl md:text-5xl' : 'text-5xl md:text-7xl'} font-black text-white dark:text-white mb-8 tracking-tighter leading-[1.1]`}>
              Real Voices, <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-green-500 to-indigo-500">Real Impact</span>
            </h1>
            <p className="text-gray-400 dark:text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed font-medium">
              Discover how SnapED helps learners retain knowledge longer and faster. Join thousands of students getting smarter every day.
            </p>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px -10px rgba(34, 197, 94, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => { setShowModal(true); setEditingId(null); }}
              className="px-10 py-4 bg-white text-black rounded-full font-black text-lg flex items-center gap-3 mx-auto hover:bg-green-50 transition-all duration-300 shadow-2xl shadow-white/10"
            >
              <AiOutlineComment size={24} /> Share Your Feedback
            </motion.button>
          </motion.div>
        </div>

        <div className="relative group min-h-[500px] flex flex-col justify-center">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-green-500/20 border-t-green-500 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <MarqueeRow items={row1} />
              <MarqueeRow items={row2} reverse />
            </>
          )}

          {/* Gradients to fade edges */}
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[var(--bg-main)] to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[var(--bg-main)] to-transparent z-10 pointer-events-none" />
        </div>
      </section>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/90 backdrop-blur-xl"
              onClick={() => setShowModal(false)}
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              className="relative w-full max-w-lg bg-[#0a0a0a] border border-white/10 rounded-[2.5rem] p-10 shadow-[0_0_100px_-20px_rgba(34,197,94,0.3)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" />

              <button onClick={() => setShowModal(false)} className="absolute top-8 right-8 text-gray-500 hover:text-white transition-colors bg-white/5 p-2 rounded-xl"><FaTimes size={18} /></button>

              <h3 className="text-3xl font-black text-white mb-2">{editingId ? 'Refine Your Review' : 'Rate Your Experience'}</h3>
              <p className="text-gray-500 text-sm mb-10 font-medium">Your insights help us build a more powerful platform for students.</p>

              <div className="flex gap-4 mb-10 justify-center">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} onClick={() => setRating(s)} className="transition-all duration-300 hover:scale-125 active:scale-90 group">
                    {s <= rating ?
                      <AiFillStar className="text-green-500 drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]" size={44} /> :
                      <AiOutlineStar className="text-gray-800 group-hover:text-gray-600" size={44} />
                    }
                  </button>
                ))}
              </div>

              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="What's your SnapED story?"
                className="w-full h-40 bg-white/5 border border-white/10 rounded-3xl p-6 text-white placeholder-gray-700 focus:border-green-500/50 focus:ring-4 focus:ring-green-500/5 outline-none mb-8 transition-all resize-none text-base"
              />

              <button
                onClick={handleSubmit}
                className="w-full py-5 bg-gradient-to-r from-green-600 to-indigo-600 rounded-3xl text-white font-black text-lg flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-green-500/20"
              >
                <FaPaperPlane /> {editingId ? 'Update My Feedback' : 'Post Feedback'}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const defaultTestimonials = [
  { id: "d1", name: "Sarah Johnson", message: "SnapED codeCampus transformed my learning experience. The resources are precisely what I needed for my semester exams!", rating: 5, role: "Computer Science Student" },
  { id: "d2", name: "Michael Chen", message: "The aesthetic and organization of this platform are top-notch. It makes studying complex technical subjects much more engaging.", rating: 5, role: "UI/UX Designer" },
  { id: "d3", name: "Aisha Patel", message: "Finally a platform that understands what students actually need. The notes are comprehensive and easy to follow.", rating: 4, role: "IT Student" },
  { id: "d4", name: "David Miller", message: "Best PYQ collection I've found so far. Really helped me during my end-term preparations.", rating: 5, role: "B.Tech Student" },
  { id: "d5", name: "Priya Sharma", message: "The video lectures integrated here are gold. Saves me hours of searching on YouTube.", rating: 5, role: "Data Science Aspirant" },
  { id: "d6", name: "James Wilson", message: "Minimalistic yet powerful. This is how educational platforms should be designed.", rating: 5, role: "Developer" }
];

export default Feedback;